package ru.villawonka.app.presentation.screens.postCard.compose.view

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Icon
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import org.jetbrains.compose.resources.painterResource
import ru.villawonka.app.common.extensions.priceFormat
import ru.villawonka.app.presentation.design.components.AppIconButton
import ru.villawonka.app.presentation.design.components.AppIconButtonType
import ru.villawonka.app.presentation.design.components.filledButtonSecondaryContentInverted
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.AppTextStyles
import ru.villawonka.app.presentation.design.theme.onSurfacePrimary
import ru.villawonka.app.presentation.design.theme.onSurfaceSecondary
import ru.villawonka.app.presentation.design.theme.surfaceLow
import ru.villawonka.app.presentation.design.theme.surfaceTop
import ru.villawonka.app.presentation.model.PostSourceUIModel
import ru.villawonka.app.presentation.model.PostUIModel
import ru.villawonka.app.presentation.sharedviews.PhotoCarouselType
import ru.villawonka.app.presentation.sharedviews.PhotoCarouselView
import ru.villawonka.app.presentation.sharedviews.postList.compose.view.FloatingBadgesView
import ru.villawonka.app.resources.AppResources
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_heart_24
import villawonka.composeapp.generated.resources.ic_heart_outline_24
import villawonka.composeapp.generated.resources.ic_map_pin_16
import villawonka.composeapp.generated.resources.ic_share_24
import villawonka.composeapp.generated.resources.ic_verification_16
import villawonka.composeapp.generated.resources.ic_warning_circle_24

@Composable
fun PostSummaryCardView(
    post: PostUIModel,
    onImageEnlargeClick: (selectedIndex: Int) -> Unit,
    onShareClick: () -> Unit,
    onLikeClick: (Boolean) -> Unit,
    onConversationDetailsClick: () -> Unit,
    onGoToSourceClick: () -> Unit,
) {
    val shape = RoundedCornerShape(
        bottomStart = 16.dp,
        bottomEnd = 16.dp
    )
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.colors.surfaceTop,
                shape = shape
            )
            .clip(shape)
    ) {
        AppSpacer(16.dp)
        BoxWithConstraints {
            PhotoCarouselView(
                photos = post.media,
                type = PhotoCarouselType.EXPANDED,
                maxWidth = maxWidth.value,
                onImageEnlargeClick = onImageEnlargeClick,
                badge = { page -> if (page == 0) FloatingBadgesView(post = post) }
            )
        }
        AppSpacer(16.dp)
        PostActionsView(
            post = post,
            onShareClick = onShareClick,
            onFavoriteClick = onLikeClick,
            onConversationDetailsClick = onConversationDetailsClick,
        )
        AppSpacer(16.dp)
        post.source?.let {
            PostSourceView(
                source = it,
                onGoToSourceClick = onGoToSourceClick
            )
            AppSpacer(16.dp)
        }
    }
}

@Composable
private fun PostSourceView(
    source: PostSourceUIModel,
    onGoToSourceClick: () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp)
            .background(
                color = MaterialTheme.colors.surfaceLow,
                shape = RoundedCornerShape(16.dp)
            )
            .clip(RoundedCornerShape(16.dp))
            .clickable(onClick = onGoToSourceClick)
            .padding(8.dp)
    ) {
        KamelImage(
            resource = { asyncPainterResource(source.imageUrl) },
            contentDescription = null,
            contentScale = ContentScale.Crop,
            onLoading = { CircularProgressIndicator({it}) },
            modifier = Modifier
                .size(48.dp)
                .clip(RoundedCornerShape(12.dp))
        )
        AppSpacer(16.dp)
        Column {
            Text(
                text = source.name,
                style = AppTextStyles.bodySmallRegular,
                color = MaterialTheme.colors.onSurfacePrimary
            )
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = AppResources.strings.goToSource,
                    style = AppTextStyles.bodySmallMedium,
                    color = MaterialTheme.colors.onSurfaceSecondary
                )
                if (source.isTrusted) {
                    AppSpacer(4.dp)
                    Icon(
                        painter = painterResource(Res.drawable.ic_verification_16),
                        contentDescription = null,
                        tint = MaterialTheme.colors.filledButtonSecondaryContentInverted
                    )
                }
            }
        }
    }
}

@Composable
private fun PostActionsView(
    post: PostUIModel,
    onShareClick: () -> Unit,
    onFavoriteClick: (Boolean) -> Unit,
    onConversationDetailsClick: () -> Unit,
) {
    var isLiked by remember { mutableStateOf(post.isLiked) }

    Row(
        modifier = Modifier.padding(horizontal = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(
            modifier = Modifier.weight(1f)
        ) {
            when (post) {
                is PostUIModel.Accommodation -> {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "${post.displayCost.priceFormat()} ${post.displayCurrency.code}",
                            style = AppTextStyles.titleMediumBold,
                            color = MaterialTheme.colors.onSurfacePrimary
                        )
                        post.conversationDetails?.let {
                            AppSpacer(8.dp)
                            Icon(
                                painter = painterResource(Res.drawable.ic_warning_circle_24),
                                contentDescription = null,
                                tint = MaterialTheme.colors.onSurfacePrimary,
                                modifier = Modifier
                                    .size(16.dp)
                                    .clickable(
                                        interactionSource = null,
                                        indication = null,
                                        onClick = onConversationDetailsClick
                                    ) ,
                            )
                        }
                    }
                    AppSpacer(8.dp)
                }
            }
            Row(
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_map_pin_16),
                    contentDescription = null,
                    tint = MaterialTheme.colors.onSurfacePrimary
                )
                AppSpacer(4.dp)
                Text(
                    text = post.location,
                    style = AppTextStyles.bodyMediumRegular,
                    color = MaterialTheme.colors.onSurfacePrimary
                )
            }
        }
        AppIconButton(
            iconRes = Res.drawable.ic_share_24,
            enabled = true,
            onClick = onShareClick
        )
        AppSpacer(12.dp)
        AppIconButton(
            iconRes = if (isLiked) Res.drawable.ic_heart_24 else Res.drawable.ic_heart_outline_24,
            enabled = true,
            selected = isLiked,
            type =  AppIconButtonType.FAVORITE,
            onClick = {
                isLiked = !isLiked
                onFavoriteClick(isLiked)
            }
        )
    }
}
